import Link from 'next/link';
import { useRouter } from 'next/router';
import React, { memo, useEffect, useRef } from 'react';

import { pushArticleViewEvent, sentClickPreviewEvent } from '../../../../helpers/analytics';
import { getMaterialLink } from '../../../../helpers/news';
import { useRubricType } from '../../../../hooks/news';
import { IRouterQuery } from '../../../../interfaces/news';
import { useWhitelabel } from '../../../../reducers/whitelabel';
import { LazyLoad } from '../../../../utils/lazyLoad';
import { IListsCardProps } from '../../../ListCards';
import { MaterialCard } from '../../../ListCards/MaterialCard';
import { IArticleProps } from '../../../MaterialContent/components/Article';
import styles from '../../list-content.module.scss';

interface IListCardProps<T> extends Pick<IArticleProps, 'material' | 'index' | 'titleTag'> {
  onClick?: () => void;
  handleShowAuthPopup?: () => void;

  color?: IListsCardProps['color'];
  size?: IListsCardProps['size'];
  direction?: IListsCardProps['direction'];
  card?: React.ComponentType<IListsCardProps>;
  wrapper?: React.ElementType<T>;
  withImage?: boolean;
}

function ListCardComponent<T>({
  index,
  material,
  onClick,
  color,
  size,
  direction,
  card: CardComponent = MaterialCard,
  wrapper: WrapperComponent = 'article',
  withImage,
  ...props
}: IListCardProps<T> & T) {
  const {
    query: { rubric, rubricAlias },
  } = useRouter() as TRouter<IRouterQuery>;

  const listCardRef = useRef<HTMLDivElement>();
  const rubricType = useRubricType();
  const isWL = useWhitelabel();

  const isNews = rubricType === 'novosti';
  const materialType = material.tags.includes('Вопрос-ответ')
    ? 'question'
    : material.tags.includes('Тесты и игры')
    ? 'test'
    : 'material';

  const { externalLink } = material;
  let href = '';
  let as = '';
  let isExternal = !!externalLink;
  if (isExternal) {
    href = externalLink;
  } else {
    const link = getMaterialLink(material, {
      ...(!isNews && (rubric || rubricAlias) && { rubricAlias: rubric || rubricAlias }),
      ...(isWL && { wl: isWL }),
    });
    href = link.href;
    as = link.as;
  }
  const getLink = {
    href,
    as,
    isExternal,
  };

  const handleSentEvent = (url: string) => () => {
    sentClickPreviewEvent(url);
    pushArticleViewEvent(material, url);
  };

  useEffect(() => {
    const lazyLoad = new LazyLoad(listCardRef.current);

    return () => {
      lazyLoad.removeObserver();
    };
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [isNews]);

  const cardContent = (
    <CardComponent
      size={size}
      material={material}
      index={index}
      materialType={materialType}
      color={color}
      direction={direction}
      withImage={withImage}
    />
  );

  return (
    //@ts-ignore
    <WrapperComponent
      data-alias={material.alias}
      className={styles.mainItem}
      ref={listCardRef}
      onClick={onClick}
      {...props}
    >
      {getLink.isExternal ? (
        <a href={getLink.href} className={styles.mainItemLink} rel="noreferrer" onClick={handleSentEvent(getLink.href)}>
          {cardContent}
        </a>
      ) : (
        <Link href={getLink.href} as={getLink.as}>
          <a className={styles.mainItemLink} onClick={handleSentEvent(getLink.as)}>
            {cardContent}
          </a>
        </Link>
      )}
    </WrapperComponent>
  );
}

export const ListCard = memo(ListCardComponent) as <T>(props: IListCardProps<T> & T) => JSX.Element;
